import { formatoNumero } from '@/lib/utils/formato-precio';

/**
 * La «vista previa a escala» de Agregar producto (maqueta): el bulto dibujado en
 * isométrico con sus proporciones reales y sus tres medidas. Sirve para ver de un
 * vistazo si se escribió el alto donde iba el largo —una caja de notebook que
 * sale como una columna se nota al instante—.
 *
 * Isométrico simple: el largo baja a la derecha, el ancho baja a la izquierda y
 * el alto sube. Se ven la tapa y las dos caras de adelante.
 */
export function CajaAEscala({
  largo,
  ancho,
  alto,
  color,
}: {
  largo: number;
  ancho: number;
  alto: number;
  color: string;
}) {
  const valido = largo > 0 && ancho > 0 && alto > 0;
  const L = valido ? largo : 40;
  const W = valido ? ancho : 30;
  const H = valido ? alto : 20;

  const c = Math.cos(Math.PI / 6);
  const s = 0.5;
  const p = (x: number, y: number, z: number): [number, number] => [(x - y) * c, (x + y) * s - z];

  const v = {
    a: p(0, 0, H),
    b: p(L, 0, H),
    cc: p(L, W, H),
    d: p(0, W, H),
    e: p(L, 0, 0),
    f: p(L, W, 0),
    g: p(0, W, 0),
  };
  const puntos = Object.values(v);
  const minX = Math.min(...puntos.map((q) => q[0]));
  const maxX = Math.max(...puntos.map((q) => q[0]));
  const minY = Math.min(...puntos.map((q) => q[1]));
  const maxY = Math.max(...puntos.map((q) => q[1]));

  // Encaja el dibujo en el lienzo dejando lugar para los rótulos.
  const ANCHO = 240;
  const ALTO = 170;
  const MARGEN = 30;
  const escala = Math.min(
    (ANCHO - MARGEN * 2) / (maxX - minX),
    (ALTO - MARGEN * 2) / (maxY - minY)
  );
  const dx = (ANCHO - (maxX - minX) * escala) / 2 - minX * escala;
  const dy = (ALTO - (maxY - minY) * escala) / 2 - minY * escala;
  const t = (q: [number, number]) => [q[0] * escala + dx, q[1] * escala + dy] as const;
  const poli = (...qs: Array<[number, number]>) => qs.map((q) => t(q).join(',')).join(' ');
  const medio = (q1: [number, number], q2: [number, number]) =>
    t([(q1[0] + q2[0]) / 2, (q1[1] + q2[1]) / 2]);

  const rotuloLargo = medio(v.g, v.f);
  const rotuloAncho = medio(v.f, v.e);
  const rotuloAlto = medio(v.d, v.g);

  return (
    <figure className="flex h-full flex-col items-center justify-center rounded-xl border bg-surface-sunken px-3 pb-3 pt-2">
      <svg
        viewBox={`0 0 ${ANCHO} ${ALTO}`}
        className="h-auto w-full max-w-[260px]"
        role="img"
        aria-label={
          valido
            ? `Bulto de ${formatoNumero(largo)} por ${formatoNumero(ancho)} por ${formatoNumero(alto)} centímetros`
            : 'Escribe las medidas para ver el bulto'
        }
      >
        <g opacity={valido ? 1 : 0.35}>
          {/* Cara del ancho (derecha), la más oscura */}
          <polygon points={poli(v.b, v.cc, v.f, v.e)} fill={color} />
          <polygon points={poli(v.b, v.cc, v.f, v.e)} fill="black" opacity={0.22} />
          {/* Cara del largo (izquierda) */}
          <polygon points={poli(v.d, v.cc, v.f, v.g)} fill={color} />
          <polygon points={poli(v.d, v.cc, v.f, v.g)} fill="black" opacity={0.08} />
          {/* Tapa, la más clara */}
          <polygon points={poli(v.a, v.b, v.cc, v.d)} fill={color} />
          <polygon points={poli(v.a, v.b, v.cc, v.d)} fill="white" opacity={0.2} />
        </g>
        {valido && (
          <g className="fill-foreground" fontSize="11" fontWeight={600}>
            <text x={rotuloLargo[0] - 6} y={rotuloLargo[1] + 18} textAnchor="middle">
              {formatoNumero(largo)} cm
            </text>
            <text x={rotuloAncho[0] + 14} y={rotuloAncho[1] + 12} textAnchor="start">
              {formatoNumero(ancho)}
            </text>
            <text x={rotuloAlto[0] - 10} y={rotuloAlto[1] + 4} textAnchor="end">
              {formatoNumero(alto)}
            </text>
          </g>
        )}
      </svg>
      <figcaption className="mt-1 text-xs text-muted-foreground">vista previa a escala</figcaption>
    </figure>
  );
}
